import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  analyzerHref,
  classifyProductIdentifier,
} from "@/lib/analyzeQuery";
import {
  formatAnalyzerHistoryWhen,
  readAnalyzerHistory,
} from "@/lib/analyzerLaunchHistory";
import { usd } from "@/lib/format";
import { ScanLine, Search, ArrowRight } from "lucide-react";
import { toast } from "sonner";

function recognitionLabel(raw) {
  const classified = classifyProductIdentifier(raw);
  if (!classified) return "";
  switch (classified.path) {
    case "walmart_url":
      return "Walmart URL";
    case "gtin_upc":
      return "UPC";
    case "walmart_item_id":
      return "Item ID";
    case "invalid_gtin":
      return "Invalid GTIN";
    case "free_text":
      return "Product name";
    default:
      return "";
  }
}

/**
 * Empty-state Product Analyzer — search to analyze; demo is opt-in only.
 */
export default function AnalyzerLaunch() {
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  const [history, setHistory] = useState(() => readAnalyzerHistory());

  useEffect(() => {
    setHistory(readAnalyzerHistory());
  }, []);

  const typeLabel = useMemo(() => recognitionLabel(query.trim()), [query]);
  const canAnalyze = Boolean(query.trim());

  const startAnalyze = (term) => {
    const value = String(term ?? query).trim();
    if (!value) return;
    const href = analyzerHref(value);
    if (!href || href === "/app/analyzer") {
      toast.error("Enter a Walmart URL, item ID, UPC, or product name.");
      return;
    }
    nav(href);
  };

  const onDemo = () => {
    nav("/app/analyzer?demo=1");
  };

  const onScan = () => {
    nav("/app/receipts/scan");
  };

  return (
    <div className="ez-al" data-testid="analyzer-launch">
      <section className="ez-al-hero">
        <div>
          <div className="ez-al-eyebrow">Walmart intelligence workspace</div>
          <h1 className="ez-al-title">Turn a product into a decision.</h1>
          <p className="ez-al-lead">
            Search by URL, item ID, UPC, barcode, or name. Ezisell verifies the live offer,
            competition, demand signals, and profit inputs before you source.
          </p>
        </div>
        <div className="ez-al-proof" aria-label="Live Walmart data">
          <i className="ez-al-pulse" aria-hidden />
          Live Walmart data
        </div>
      </section>

      <section className="ez-al-search-card">
        <div className="ez-al-search-row">
          <label className="ez-al-input-shell">
            <Search className="ez-al-search-icon" aria-hidden />
            <input
              data-testid="analyzer-launch-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canAnalyze) {
                  e.preventDefault();
                  startAnalyze();
                }
              }}
              autoComplete="off"
              placeholder="Walmart URL, item ID, UPC, barcode, or product name"
              aria-label="Product identifier"
            />
          </label>
          <button
            type="button"
            className="ez-al-icon-btn"
            title="Scan barcode"
            aria-label="Scan barcode"
            data-testid="analyzer-launch-scan"
            onClick={onScan}
          >
            <ScanLine className="h-5 w-5" />
          </button>
          <button
            type="button"
            className="ez-al-analyze"
            data-testid="analyzer-launch-analyze"
            disabled={!canAnalyze}
            onClick={() => startAnalyze()}
          >
            Analyze product
          </button>
        </div>

        <div className="ez-al-search-meta">
          <div className="ez-al-recognition">
            <span>Press Enter to analyze</span>
            {typeLabel ? (
              <b className="ez-al-type-chip" data-testid="analyzer-launch-type">
                {typeLabel}
              </b>
            ) : null}
          </div>
          <button
            type="button"
            className="ez-al-demo"
            data-testid="analyzer-launch-demo"
            onClick={onDemo}
          >
            Explore with demo data
          </button>
        </div>

        <div className="ez-al-trust-row">
          <div className="ez-al-trust">
            <b>Live PDP verification</b>
            <span>Price, stock, seller and fulfillment</span>
          </div>
          <div className="ez-al-trust">
            <b>Competition intelligence</b>
            <span>Seller count and offer pressure</span>
          </div>
          <div className="ez-al-trust">
            <b>Profit-ready inputs</b>
            <span>Fees, max cost and ROI scenario</span>
          </div>
        </div>
      </section>

      <section className="ez-al-history">
        <div className="ez-al-section-head">
          <div>
            <div className="ez-al-eyebrow">Your workspace</div>
            <h2>Analysis history</h2>
          </div>
          <Link
            to="/app/history"
            className="ez-al-history-link"
            data-testid="analyzer-launch-history-all"
          >
            View all history →
          </Link>
        </div>
        <div className="ez-al-list" data-testid="analyzer-launch-history-list">
          {history.map((item) => {
            const analyzeKey = item.itemId || item.query || item.id;
            const priceLabel =
              typeof item.price === "number" ? usd(item.price) : item.price || "—";
            return (
              <article
                key={item.id}
                className="ez-al-item"
                tabIndex={0}
                role="button"
                data-testid={`analyzer-history-item-${item.id}`}
                onClick={() => startAnalyze(analyzeKey)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    startAnalyze(analyzeKey);
                  }
                }}
              >
                {item.image ? (
                  <img className="ez-al-thumb" src={item.image} alt="" />
                ) : (
                  <div className="ez-al-thumb ez-al-thumb-empty" aria-hidden />
                )}
                <div className="ez-al-item-body">
                  <h3>{item.title}</h3>
                  <div className="ez-al-meta">
                    <span>
                      {item.itemId
                        ? `Walmart item ${item.itemId}`
                        : item.query || "Lookup"}
                    </span>
                    <span>{formatAnalyzerHistoryWhen(item.analyzedAt)}</span>
                  </div>
                  <div className="ez-al-signals">
                    {(item.signals || []).map((s) => (
                      <span
                        key={s}
                        className={
                          /in stock/i.test(s) ? "ez-al-signal good" : "ez-al-signal"
                        }
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
                <span className="ez-al-live">Live</span>
                <div className="ez-al-price">
                  {priceLabel}
                  <small>Analyze again</small>
                </div>
                <span className="ez-al-arrow" aria-hidden>
                  <ArrowRight className="h-4 w-4" />
                </span>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
